import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Activity,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  MapPin,
  Smartphone,
  Wifi,
  Lock,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Timer,
  Building,
  Zap,
  Radio,
  X,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Skeleton } from '@/components/ui/Skeleton';
import { Modal } from '@/components/ui';
import { Column } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useAuth } from '@/contexts/AuthContext';
import {
  getFirestore,
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  onSnapshot,
  where,
  Timestamp,
} from 'firebase/firestore';
import {
  useDataTableFilters,
  DataTableFilter,
  createColumnConfigHelper,
  applyFilters,
} from '@/components/data-table-filter';

// ============================================================================
// Types
// ============================================================================

interface MonitorExecution {
  id: string;
  flowId: string;
  flowName: string;
  organizationId: string;
  userId?: string;
  status: string;
  startedAt: any; // Timestamp
  completedAt?: any;
  duration?: number;
  success?: boolean;
  errorMessage?: string;
  error?: string;
  // Location
  locationId?: string;
  locationName?: string;
  regionName?: string;
  countryCode?: string;
  // Modem
  modemId?: string;
  modemImei?: string;
  modemModel?: string;
  modemName?: string;
  deviceId?: string;
  // eSIM
  esimProfileId?: string;
  esimProfileName?: string;
  esimNetworkId?: string;
  networkName?: string;
  networkMcc?: string;
  networkMnc?: string;
  // Attached network (from AT+COPS during execution)
  registeredNetwork?: string;
  registeredNetworkName?: string;
  registeredNetworkType?: string;
  registeredNetworkMcc?: string;
  registeredNetworkMnc?: string;
  // Meta
  source?: string;
  triggerType?: string;
  isClusterChild?: boolean;
  parentExecutionId?: string;
  inputs?: Record<string, any>;
  outputs?: Record<string, any>;
  logs?: any[];
}

interface ResourceLock {
  id: string;
  resourceType: string;
  resourceId: string;
  lockType: string;
  executionId?: string;
  sessionId?: string;
  userId?: string;
  organizationId?: string;
  lockedAt: any;
  expiresAt: any;
  ttl?: number;
  metadata?: Record<string, any>;
}

// ============================================================================
// Helpers
// ============================================================================

function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  if (typeof value === 'string') return new Date(value);
  return null;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'completed': return 'bg-status-success-bg text-status-success';
    case 'failed': return 'bg-destructive/10 text-destructive';
    case 'running': return 'bg-accent-blue/10 text-accent-blue';
    case 'pending':
    case 'initializing': return 'bg-yellow-500/10 text-yellow-600';
    case 'cancelled': return 'bg-muted text-muted-foreground';
    default: return 'bg-muted text-muted-foreground';
  }
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed': return <CheckCircle2 className="w-3.5 h-3.5" />;
    case 'failed': return <XCircle className="w-3.5 h-3.5" />;
    case 'running': return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    case 'pending':
    case 'initializing': return <Clock className="w-3.5 h-3.5" />;
    case 'cancelled': return <X className="w-3.5 h-3.5" />;
    default: return <AlertCircle className="w-3.5 h-3.5" />;
  }
}

// ============================================================================
// Filter Config
// ============================================================================

const filterColumnHelper = createColumnConfigHelper<MonitorExecution>();

const filterColumnsConfig = [
  filterColumnHelper
    .option()
    .id('status')
    .accessor((row) => row.status)
    .displayName('Status')
    .icon(Activity)
    .options([
      { label: 'Pending', value: 'pending' },
      { label: 'Running', value: 'running' },
      { label: 'Completed', value: 'completed' },
      { label: 'Failed', value: 'failed' },
      { label: 'Cancelled', value: 'cancelled' },
    ])
    .build(),
  filterColumnHelper
    .text()
    .id('locationName')
    .accessor((row) => row.locationName || '')
    .displayName('Location')
    .icon(MapPin)
    .build(),
  filterColumnHelper
    .text()
    .id('flowName')
    .accessor((row) => row.flowName || '')
    .displayName('Flow')
    .icon(Zap)
    .build(),
  filterColumnHelper
    .text()
    .id('orgName')
    .accessor(() => '') // Will be resolved via orgMap
    .displayName('Organization')
    .icon(Building)
    .build(),
  filterColumnHelper
    .option()
    .id('source')
    .accessor((row) => row.source || row.triggerType || 'manual')
    .displayName('Source')
    .icon(Radio)
    .options([
      { label: 'Quick Test', value: 'quick_test' },
      { label: 'API', value: 'api' },
      { label: 'Manual', value: 'manual' },
      { label: 'Scheduled', value: 'scheduled' },
    ])
    .build(),
] as const;

// ============================================================================
// Component
// ============================================================================

export function ExecutionMonitorPage() {
  const { isSuperAdmin } = useAuth();
  const fmt = useDateFormat();
  const [executions, setExecutions] = useState<MonitorExecution[]>([]);
  const [locks, setLocks] = useState<ResourceLock[]>([]);
  const [orgMap, setOrgMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [showResources, setShowResources] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState<MonitorExecution | null>(null);

  const db = getFirestore();

  // Live clock for running duration display
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Load organization names (one-time)
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'organizations'));
        const map = new Map<string, string>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          map.set(doc.id, data.name || data.displayName || doc.id);
        });
        setOrgMap(map);
      } catch (err) {
        console.error('Failed to load organizations:', err);
      }
    };
    loadOrgs();
  }, []);

  // Real-time executions listener
  useEffect(() => {
    if (!isSuperAdmin) return;

    const q = query(
      collection(db, 'executions'),
      orderBy('startedAt', 'desc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data: MonitorExecution[] = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }) as MonitorExecution)
          .filter((exec) =>
            // Filter out child executions (primitive sub-calls and cluster children)
            !exec.isClusterChild &&
            !exec.parentExecutionId &&
            !exec.id.includes('_primitive_')
          );
        setExecutions(data);
        setLoading(false);
      },
      (err) => {
        console.error('Executions listener error:', err);
        setError('Failed to load executions: ' + err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isSuperAdmin]);

  // Real-time resource locks listener
  useEffect(() => {
    if (!isSuperAdmin) return;

    const q = query(
      collection(db, 'resource_locks'),
      orderBy('lockedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data: ResourceLock[] = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }) as ResourceLock)
          .filter((lock) => {
            const expires = toDate(lock.expiresAt);
            return expires && expires.getTime() > Date.now();
          });
        setLocks(data);
      },
      (err) => {
        console.error('Locks listener error:', err);
      }
    );

    return () => unsubscribe();
  }, [isSuperAdmin]);

  // Enrich executions with org names for filtering
  const enrichedExecutions = useMemo(() => {
    return executions.map((exec) => ({
      ...exec,
      _orgName: orgMap.get(exec.organizationId) || exec.organizationId,
    }));
  }, [executions, orgMap]);

  // Override filter config to use orgName from map
  const dynamicFilterConfig = useMemo(() => {
    return filterColumnsConfig.map((col) => {
      if (col.id === 'orgName') {
        return {
          ...col,
          accessor: (row: any) => row._orgName || '',
        };
      }
      return col;
    });
  }, []);

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: enrichedExecutions,
    columnsConfig: dynamicFilterConfig,
  });

  const filteredExecutions = useMemo(
    () => applyFilters(enrichedExecutions, filters, dynamicFilterConfig),
    [enrichedExecutions, filters, dynamicFilterConfig]
  );

  // Stats
  const stats = useMemo(() => {
    const running = executions.filter(
      (e) => e.status === 'running' || e.status === 'pending' || e.status === 'initializing'
    ).length;
    const completed = executions.filter((e) => e.status === 'completed').length;
    const failed = executions.filter((e) => e.status === 'failed').length;
    const successful = executions.filter(
      (e) => e.status === 'completed' && e.success
    ).length;
    const successRate =
      completed + failed > 0
        ? Math.round((successful / (completed + failed)) * 100)
        : 0;
    const withDuration = executions.filter((e) => e.duration && e.duration > 0);
    const avgDuration =
      withDuration.length > 0
        ? withDuration.reduce((sum, e) => sum + (e.duration || 0), 0) / withDuration.length
        : 0;

    return { total: executions.length, running, successRate, avgDuration };
  }, [executions]);

  // Get live duration for a running execution
  const getLiveDuration = useCallback(
    (exec: MonitorExecution): string => {
      if (exec.duration) return formatDuration(exec.duration);
      const started = toDate(exec.startedAt);
      if (!started) return '-';
      if (exec.status === 'running' || exec.status === 'pending' || exec.status === 'initializing') {
        return formatDuration(now - started.getTime());
      }
      return '-';
    },
    [now]
  );

  // Table columns
  const columns: Column<MonitorExecution & { _orgName: string }>[] = [
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_, exec) => (
        <Badge
          variant="secondary"
          className={`${getStatusColor(exec.status)} gap-1 font-medium`}
        >
          {getStatusIcon(exec.status)}
          {exec.status}
        </Badge>
      ),
    },
    {
      key: 'flowName',
      header: 'Flow',
      sortable: true,
      render: (_, exec) => (
        <div className="text-sm max-w-[200px]">
          <div className="font-medium text-foreground truncate">{exec.flowName}</div>
          {exec.isClusterChild && (
            <Badge variant="outline" className="text-[10px] mt-0.5">cluster</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'organizationId',
      header: 'Organization',
      sortable: true,
      render: (_, exec) => (
        <div className="text-sm">
          <div className="font-medium text-foreground">
            {exec._orgName}
          </div>
        </div>
      ),
    },
    {
      key: 'locationName',
      header: 'Location',
      sortable: true,
      render: (_, exec) => (
        exec.locationName ? (
          <div className="text-sm">
            <div className="font-medium text-foreground flex items-center gap-1">
              <MapPin className="w-3 h-3 text-muted-foreground" />
              {exec.locationName}
            </div>
            {(exec.regionName || exec.countryCode) && (
              <div className="text-muted-foreground/70 text-xs">
                {[exec.regionName, exec.countryCode].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground/50 text-sm">-</span>
        )
      ),
    },
    {
      key: 'modemModel',
      header: 'Modem',
      render: (_, exec) => {
        const hasModel = exec.modemModel && exec.modemModel !== 'Unknown Model';
        const hasImei = !!exec.modemImei;
        if (!hasModel && !hasImei) {
          return <span className="text-muted-foreground/50 text-sm">-</span>;
        }
        return (
          <div className="text-sm">
            <div className="font-medium text-foreground flex items-center gap-1">
              <Smartphone className="w-3 h-3 text-muted-foreground" />
              {hasModel ? exec.modemModel : exec.modemImei}
            </div>
            {hasModel && hasImei && (
              <div className="text-muted-foreground/70 text-xs font-mono">
                {exec.modemImei}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'esimProfileName',
      header: 'eSIM / Network',
      render: (_, exec) => {
        const hasEsim = !!exec.esimProfileName;
        const hasRegistered = !!exec.registeredNetwork;
        if (!hasEsim && !hasRegistered) {
          return <span className="text-muted-foreground/50 text-sm">-</span>;
        }
        return (
          <div className="text-sm">
            {hasEsim && (
              <div className="font-medium text-foreground flex items-center gap-1">
                <Wifi className="w-3 h-3 text-muted-foreground" />
                {exec.esimProfileName}
              </div>
            )}
            {hasRegistered && (
              <div className="text-muted-foreground/70 text-xs flex items-center gap-1">
                <Radio className="w-3 h-3" />
                {exec.registeredNetworkName || exec.registeredNetwork}
                {exec.registeredNetworkName && exec.registeredNetworkName !== exec.registeredNetwork && (
                  <span className="text-muted-foreground/50">({exec.registeredNetwork})</span>
                )}
              </div>
            )}
            {!hasRegistered && exec.networkName && (
              <div className="text-muted-foreground/70 text-xs">
                {exec.networkName}
                {exec.networkMcc && ` (${exec.networkMcc}/${exec.networkMnc})`}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'duration',
      header: 'Duration',
      sortable: true,
      render: (_, exec) => {
        const isLive = exec.status === 'running' || exec.status === 'pending' || exec.status === 'initializing';
        return (
          <div className="text-sm font-mono">
            <span className={isLive ? 'text-accent-blue' : 'text-foreground'}>
              {getLiveDuration(exec)}
            </span>
            {isLive && <Loader2 className="w-3 h-3 inline ml-1 animate-spin text-accent-blue" />}
          </div>
        );
      },
    },
    {
      key: 'startedAt',
      header: 'Started',
      sortable: true,
      render: (_, exec) => {
        const started = toDate(exec.startedAt);
        return started ? (
          <div className="text-sm text-muted-foreground">
            {formatRelativeTime(started)}
          </div>
        ) : (
          <span className="text-muted-foreground/50 text-sm">-</span>
        );
      },
    },
    {
      key: 'startedAtAbsolute',
      header: 'Date',
      render: (_, exec) => {
        const started = toDate(exec.startedAt);
        return started ? (
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            {fmt.formatDateTime(started)}
          </div>
        ) : (
          <span className="text-muted-foreground/50 text-sm">-</span>
        );
      },
    },
    {
      key: 'source',
      header: 'Source',
      render: (_, exec) => {
        const src = exec.source || exec.triggerType || 'manual';
        const srcLabel = src === 'quick_test' ? 'Quick Test' : src;
        return (
          <Badge variant="outline" className="text-xs capitalize">
            {srcLabel}
          </Badge>
        );
      },
    },
  ];

  // ============================================================================
  // Detail Modal
  // ============================================================================

  const renderDetailModal = () => {
    if (!selectedExecution) return null;
    const exec = selectedExecution;
    const started = toDate(exec.startedAt);
    const completed = toDate(exec.completedAt);
    const logs = exec.logs || [];
    // Voice-agent transcript lives in structured output (outputs.<var>.transcript,
    // e.g. agentResult), NOT in logs[] — the log panel only carries the summary.
    // Surface it as a chat panel. Secrets are already redacted upstream ([DTMF ***]).
    const voiceTranscript: Array<{ role?: string; text?: string }> | null = (() => {
      const outs = exec.outputs;
      if (!outs || typeof outs !== 'object') return null;
      for (const v of Object.values(outs)) {
        if (v && typeof v === 'object' && Array.isArray((v as any).transcript) && (v as any).transcript.length) {
          return (v as any).transcript as Array<{ role?: string; text?: string }>;
        }
      }
      return null;
    })();

    return (
      <Modal
        isOpen={!!selectedExecution}
        onClose={() => setSelectedExecution(null)}
        title={`Execution: ${exec.flowName}`}
        size="lg"
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Status + Result */}
          <div className="flex items-center gap-3">
            <Badge
              variant="secondary"
              className={`${getStatusColor(exec.status)} gap-1 text-sm`}
            >
              {getStatusIcon(exec.status)}
              {exec.status}
            </Badge>
            {exec.success !== undefined && (
              <Badge variant={exec.success ? 'secondary' : 'destructive'}>
                {exec.success ? 'Success' : 'Failed'}
              </Badge>
            )}
            {exec.source && (
              <Badge variant="outline" className="capitalize">
                {exec.source === 'quick_test' ? 'Quick Test' : exec.source}
              </Badge>
            )}
          </div>

          {/* Error */}
          {(exec.errorMessage || exec.error) && (
            <Alert variant="destructive">
              <AlertDescription className="font-mono text-xs">
                {exec.errorMessage || exec.error}
              </AlertDescription>
            </Alert>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-1">Flow</div>
              <div className="font-medium text-foreground">{exec.flowName}</div>
              <div className="text-muted-foreground/70 font-mono text-xs">{exec.flowId}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-1">Organization</div>
              <div className="font-medium text-foreground">
                {orgMap.get(exec.organizationId) || exec.organizationId}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-1">Location</div>
              <div className="font-medium text-foreground">{exec.locationName || '-'}</div>
              {exec.regionName && (
                <div className="text-muted-foreground/70 text-xs">{exec.regionName}</div>
              )}
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-1">Modem</div>
              <div className="font-medium text-foreground">
                {exec.modemModel || exec.modemName || exec.modemImei || '-'}
              </div>
              {exec.modemImei && (
                <div className="text-muted-foreground/70 font-mono text-xs">{exec.modemImei}</div>
              )}
              {exec.deviceId && (
                <div className="text-muted-foreground/70 font-mono text-xs">Device: {exec.deviceId}</div>
              )}
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-1">eSIM Profile</div>
              <div className="font-medium text-foreground">{exec.esimProfileName || '-'}</div>
              {exec.networkName && (
                <div className="text-muted-foreground/70 text-xs">
                  {exec.networkName}
                  {exec.networkMcc && ` (MCC: ${exec.networkMcc}, MNC: ${exec.networkMnc})`}
                </div>
              )}
              {exec.registeredNetwork && (
                <div className="text-muted-foreground/70 text-xs mt-1 flex items-center gap-1">
                  <Radio className="w-3 h-3" />
                  Attached: {exec.registeredNetworkName || exec.registeredNetwork}
                  {exec.registeredNetworkName && exec.registeredNetworkName !== exec.registeredNetwork && ` (${exec.registeredNetwork})`}
                  {exec.registeredNetworkType && ` · ${exec.registeredNetworkType}`}
                </div>
              )}
            </div>
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-1">Timing</div>
              <div className="text-foreground">
                {started ? fmt.formatDateTime(started) : '-'}
              </div>
              {completed && (
                <div className="text-muted-foreground/70 text-xs">
                  Completed: {fmt.formatDateTime(completed)}
                </div>
              )}
              {exec.duration && (
                <div className="text-muted-foreground/70 text-xs">
                  Duration: {formatDuration(exec.duration)}
                </div>
              )}
            </div>
          </div>

          {/* Execution ID */}
          <div>
            <div className="text-muted-foreground text-xs uppercase mb-1">Execution ID</div>
            <div className="font-mono text-xs text-muted-foreground break-all">{exec.id}</div>
            {exec.parentExecutionId && (
              <div className="font-mono text-xs text-muted-foreground/70 mt-1">
                Parent: {exec.parentExecutionId}
              </div>
            )}
          </div>

          {/* Voice-agent call transcript */}
          {voiceTranscript && (
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-2">
                Call Transcript ({voiceTranscript.length} turns)
              </div>
              <div className="bg-muted/50 rounded-md p-3 max-h-[320px] overflow-y-auto space-y-2">
                {voiceTranscript.map((t, i) => {
                  const role = (t.role || '').toLowerCase();
                  const isAgent = role === 'agent';
                  const isSystem = role === 'system';
                  if (isSystem) {
                    return (
                      <div key={i} className="text-center text-xs text-muted-foreground italic px-4 py-1">
                        {t.text}
                      </div>
                    );
                  }
                  return (
                    <div key={i} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[78%] rounded-lg px-3 py-1.5 text-sm ${
                          isAgent
                            ? 'bg-accent-blue/15 text-foreground'
                            : 'bg-card border border-border text-foreground'
                        }`}
                      >
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-0.5">
                          {isAgent ? 'AI agent' : 'Far side'}
                        </div>
                        {t.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Logs */}
          {logs.length > 0 && (
            <div>
              <div className="text-muted-foreground text-xs uppercase mb-2">
                Execution Logs ({logs.length})
              </div>
              <div className="bg-muted/50 rounded-md p-3 max-h-[300px] overflow-y-auto">
                <div className="space-y-0.5 font-mono text-xs">
                  {logs.map((log, i) => {
                    // Handle both string[] and {timestamp, level, message}[] formats
                    if (typeof log === 'string') {
                      return (
                        <div key={i} className="text-muted-foreground whitespace-pre-wrap">
                          {log}
                        </div>
                      );
                    }
                    const levelColor =
                      log.level === 'error'
                        ? 'text-destructive'
                        : log.level === 'warning'
                        ? 'text-yellow-600'
                        : log.level === 'info'
                        ? 'text-foreground'
                        : 'text-muted-foreground/70';
                    return (
                      <div key={i} className={`${levelColor} whitespace-pre-wrap`}>
                        <span className="text-muted-foreground/50">
                          {log.timestamp
                            ? fmt.formatTime(log.timestamp)
                            : ''}
                        </span>{' '}
                        {log.message}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              Execution Monitor
            </h1>
            <p className="text-body text-muted-foreground">
              Real-time view of all test executions across all tenants
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="gap-1">
              <div className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
              Live
            </Badge>
          </div>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Card className="p-6">
            <div className="space-y-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Stats Cards */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-accent-blue" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">
                    Total Executions
                  </div>
                  <div className="text-h2 font-mono font-bold text-foreground">
                    {stats.total}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-accent-blue/30">
              <div className="flex items-center gap-3">
                <Loader2 className={`w-5 h-5 text-accent-blue ${stats.running > 0 ? 'animate-spin' : ''}`} />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">
                    Running Now
                  </div>
                  <div className="text-h2 font-mono font-bold text-accent-blue">
                    {stats.running}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-accent-green" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">
                    Success Rate
                  </div>
                  <div className="text-h2 font-mono font-bold text-accent-green">
                    {stats.successRate}%
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <Timer className="w-5 h-5 text-accent-purple" />
                <div>
                  <div className="text-caption text-muted-foreground uppercase">
                    Avg Duration
                  </div>
                  <div className="text-h2 font-mono font-bold text-accent-purple">
                    {stats.avgDuration > 0 ? formatDuration(stats.avgDuration) : '-'}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Active Resources Panel */}
        {!loading && (
          <Card className="overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
              onClick={() => setShowResources(!showResources)}
            >
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-foreground">Active Resources</span>
                <Badge variant="secondary" className="ml-2">
                  {locks.length} lock{locks.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              {showResources ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {showResources && (
              <div className="border-t border-border p-4">
                {locks.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4">
                    No active resource locks
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {locks.map((lock) => {
                      const expires = toDate(lock.expiresAt);
                      const remaining = expires
                        ? Math.max(0, expires.getTime() - now)
                        : 0;
                      return (
                        <div
                          key={lock.id}
                          className="border border-border rounded-lg p-3 bg-card"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                              <Lock className="w-3.5 h-3.5 text-accent-orange" />
                              <span className="font-mono text-sm font-medium text-foreground">
                                {lock.resourceId}
                              </span>
                            </div>
                            <Badge
                              variant="outline"
                              className={
                                lock.lockType === 'interactive'
                                  ? 'border-accent-purple text-accent-purple text-[10px]'
                                  : 'text-[10px]'
                              }
                            >
                              {lock.lockType}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            {lock.executionId && (
                              <div>
                                Execution:{' '}
                                <span className="font-mono">{lock.executionId.substring(0, 20)}...</span>
                              </div>
                            )}
                            {lock.organizationId && (
                              <div>
                                Org: {orgMap.get(lock.organizationId) || lock.organizationId}
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              TTL: {formatDuration(remaining)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Execution Log Table */}
        {!loading && (
          <Card className="p-6">
            <div className="mb-4 pb-4 border-b border-border">
              <DataTableFilter
                columns={filterColumns}
                filters={filters}
                actions={actions}
                strategy={strategy}
              />
            </div>
            <DataTable
              columns={columns}
              data={filteredExecutions}
              keyExtractor={(exec) => exec.id}
              searchable
              searchPlaceholder="Search by flow, location, modem, organization..."
              paginated
              defaultPageSize={25}
              emptyMessage="No executions found"
              onRowClick={(exec) => setSelectedExecution(exec)}
            />
          </Card>
        )}

        {/* Detail Modal */}
        {renderDetailModal()}
      </div>
    </AppShell>
  );
}
